import { css, cva } from "styled-system/css";

/**
 * The styles for the focus ring pseudo-element
 *
 * WARNING:
 * This value cannot be exported and used in other modules, Panda CSS will not be able to track it
 * and the styles will not be applied. Only use this value in this file.
 */
const focusRingStyles = css.raw({
  content: '""',
  display: "block",
  position: "absolute",
  top: "[-5px]", // 2px for focus ring + 2px offset + 1px to account for element border
  left: "[-5px]", // 2px for focus ring + 2px offset + 1px to account for element border
  height: "[calc(100% + 10px)]", // height + offset * 2
  width: "[calc(100% + 10px)]", // width + offset * 2
  borderStyle: "solid",
  borderWidth: "200",
  borderColor: "border.focus",
});

/**
 * A recipe for displaying a focus ring around the element during keyboard navigation.
 *
 * This only shows focus rings when the user is navigating with the keyboard (Tab, arrow keys),
 * not when elements are focused programmatically (e.g., when modals/popovers open via click).
 *
 * Requires `FocusVisibleProvider` to be mounted in the app for proper keyboard detection.
 */
export const focusRing = cva({
  base: {
    position: "relative",
    _focusVisible: {
      outline: "none",
    },
    _keyboardMode: {
      _focusVisible: {
        _before: { ...focusRingStyles },
      },
    },
  },
  variants: {
    rounded: {
      // Unfortunately we can't create this map by looping through the radius tokens because
      // Panda CSS's compiler won't be able to parse it at build time
      "0": { _keyboardMode: { _focusVisible: { _before: { rounded: "0" } } } },
      "50": { _keyboardMode: { _focusVisible: { _before: { rounded: "50" } } } },
      "100": { _keyboardMode: { _focusVisible: { _before: { rounded: "100" } } } },
      "200": { _keyboardMode: { _focusVisible: { _before: { rounded: "200" } } } },
      "250": { _keyboardMode: { _focusVisible: { _before: { rounded: "250" } } } },
      "300": { _keyboardMode: { _focusVisible: { _before: { rounded: "300" } } } },
      "350": { _keyboardMode: { _focusVisible: { _before: { rounded: "350" } } } },
      "400": { _keyboardMode: { _focusVisible: { _before: { rounded: "400" } } } },
      full: { _keyboardMode: { _focusVisible: { _before: { rounded: "full" } } } },
    },
  },
  defaultVariants: { rounded: "250" },
});

/**
 * A recipe for displaying a focus ring around the element when one of its descendants
 * is focused during keyboard navigation.
 *
 * Requires `FocusVisibleProvider` to be mounted in the app for proper keyboard detection.
 */
export const focusWithinRing = cva({
  base: {
    position: "relative",
    _focusVisibleWithin: {
      outline: "none",
    },
    _keyboardMode: {
      _focusVisibleWithin: {
        _before: { ...focusRingStyles },
      },
    },
  },
  variants: {
    rounded: {
      // Unfortunately we can't create this map by looping through the radius tokens because
      // Panda CSS's compiler won't be able to parse it at build time
      "0": { _keyboardMode: { _focusVisibleWithin: { _before: { rounded: "0" } } } },
      "50": { _keyboardMode: { _focusVisibleWithin: { _before: { rounded: "50" } } } },
      "100": { _keyboardMode: { _focusVisibleWithin: { _before: { rounded: "100" } } } },
      "200": { _keyboardMode: { _focusVisibleWithin: { _before: { rounded: "200" } } } },
      "250": { _keyboardMode: { _focusVisibleWithin: { _before: { rounded: "250" } } } },
      "300": { _keyboardMode: { _focusVisibleWithin: { _before: { rounded: "300" } } } },
      "350": { _keyboardMode: { _focusVisibleWithin: { _before: { rounded: "350" } } } },
      "400": { _keyboardMode: { _focusVisibleWithin: { _before: { rounded: "400" } } } },
      full: { _keyboardMode: { _focusVisibleWithin: { _before: { rounded: "full" } } } },
    },
  },
  defaultVariants: { rounded: "250" },
});

/**
 * A recipe for displaying a focus ring around the element during keyboard navigation.
 * This specific focus ring is inset and will not extend past the boundaries of the focused element.
 *
 * Requires `FocusVisibleProvider` to be mounted in the app for proper keyboard detection.
 */
export const insetFocusRing = cva({
  base: {
    position: "relative",
    _focusVisible: {
      outline: "none",
    },
    _keyboardMode: {
      _focusVisible: {
        _before: {
          ...focusRingStyles,
          top: "0",
          left: "0",
          height: "full",
          width: "full",
        },
      },
    },
  },
  variants: {
    rounded: {
      // Unfortunately we can't create this map by looping through the radius tokens because
      // Panda CSS's compiler won't be able to parse it at build time
      "0": { _keyboardMode: { _focusVisible: { _before: { rounded: "0" } } } },
      "50": { _keyboardMode: { _focusVisible: { _before: { rounded: "50" } } } },
      "100": { _keyboardMode: { _focusVisible: { _before: { rounded: "100" } } } },
      "200": { _keyboardMode: { _focusVisible: { _before: { rounded: "200" } } } },
      "250": { _keyboardMode: { _focusVisible: { _before: { rounded: "250" } } } },
      "300": { _keyboardMode: { _focusVisible: { _before: { rounded: "300" } } } },
      "350": { _keyboardMode: { _focusVisible: { _before: { rounded: "350" } } } },
      "400": { _keyboardMode: { _focusVisible: { _before: { rounded: "400" } } } },
      full: { _keyboardMode: { _focusVisible: { _before: { rounded: "full" } } } },
    },
  },
  defaultVariants: { rounded: "250" },
});

/**
 * A recipe for displaying a focus ring around the element during keyboard navigation.
 * This specific focus ring is inset and will not extend past the boundaries of the focused element.
 * This recipe is intended for use with elements that have a border.
 *
 * Requires `FocusVisibleProvider` to be mounted in the app for proper keyboard detection.
 */
export const insetBorderFocusRing = cva({
  base: {
    position: "relative",
    _focusVisible: {
      outline: "none",
    },
    _keyboardMode: {
      _focusVisible: {
        _before: {
          ...focusRingStyles,
          top: "-1",
          left: "-1",
          height: "[calc(100% + 2px)]",
          width: "[calc(100% + 2px)]",
        },
      },
    },
  },
  variants: {
    rounded: {
      // Unfortunately we can't create this map by looping through the radius tokens because
      // Panda CSS's compiler won't be able to parse it at build time
      "0": { _keyboardMode: { _focusVisible: { _before: { rounded: "0" } } } },
      "50": { _keyboardMode: { _focusVisible: { _before: { rounded: "50" } } } },
      "100": { _keyboardMode: { _focusVisible: { _before: { rounded: "100" } } } },
      "200": { _keyboardMode: { _focusVisible: { _before: { rounded: "200" } } } },
      "250": { _keyboardMode: { _focusVisible: { _before: { rounded: "250" } } } },
      "300": { _keyboardMode: { _focusVisible: { _before: { rounded: "300" } } } },
      "350": { _keyboardMode: { _focusVisible: { _before: { rounded: "350" } } } },
      "400": { _keyboardMode: { _focusVisible: { _before: { rounded: "400" } } } },
      full: { _keyboardMode: { _focusVisible: { _before: { rounded: "full" } } } },
    },
  },
  defaultVariants: { rounded: "250" },
});

/**
 * A recipe for displaying a focus ring around the element during keyboard navigation.
 * This specific focus ring is intended for use with tab triggers.
 *
 * Requires `FocusVisibleProvider` to be mounted in the app for proper keyboard detection.
 */
export const tabFocusRing = cva({
  base: {
    position: "relative",
    _focusVisible: {
      outline: "none",
    },
    _keyboardMode: {
      _focusVisible: {
        _before: {
          ...focusRingStyles,
          top: "2",
          left: "0",
          height: "[calc(100% - 6px)]",
          width: "full",
        },
      },
    },
  },
  variants: {
    rounded: {
      // Unfortunately we can't create this map by looping through the radius tokens because
      // Panda CSS's compiler won't be able to parse it at build time
      "0": { _keyboardMode: { _focusVisible: { _before: { rounded: "0" } } } },
      "50": { _keyboardMode: { _focusVisible: { _before: { rounded: "50" } } } },
      "100": { _keyboardMode: { _focusVisible: { _before: { rounded: "100" } } } },
      "200": { _keyboardMode: { _focusVisible: { _before: { rounded: "200" } } } },
      "250": { _keyboardMode: { _focusVisible: { _before: { rounded: "250" } } } },
      "300": { _keyboardMode: { _focusVisible: { _before: { rounded: "300" } } } },
      "350": { _keyboardMode: { _focusVisible: { _before: { rounded: "350" } } } },
      "400": { _keyboardMode: { _focusVisible: { _before: { rounded: "400" } } } },
      full: { _keyboardMode: { _focusVisible: { _before: { rounded: "full" } } } },
    },
  },
  defaultVariants: { rounded: "50" },
});

/**
 * A recipe for displaying a focus ring around a menu item or any element that makes use of a
 * `data-highlight` attribute and has an ancestor in the `:focus-visible` state.
 * This recipe is mainly intended for use with our `menuItem` recipe.
 *
 * Only shows focus rings during keyboard navigation.
 * Requires `FocusVisibleProvider` to be mounted in the app for proper keyboard detection.
 */
export const menuItemFocusRing = cva({
  base: {
    position: "relative",
    "*:focus-visible &": {
      "&[data-highlighted]:not(:hover)": {
        outline: "none",
      },
    },
    "html[data-focus-visible-mode='keyboard'] *:focus-visible &": {
      "&[data-highlighted]:not(:hover)": {
        _before: {
          ...focusRingStyles,
          // Unfortunately since menu items sit inside a scrolling list, the focus ring cannot
          // extend pass the boundary of its container. These overrides move the focus ring 2px
          // inside of the element instead.
          top: "2",
          left: "2",
          height: "[calc(100% - 4px)]",
          width: "[calc(100% - 4px)]",
        },
      },
    },
  },
  variants: {
    rounded: {
      // Unfortunately we can't create this map by looping through the radius tokens because
      // Panda CSS's compiler won't be able to parse it at build time
      "0": { "&[data-highlighted]": { _before: { rounded: "0" } } },
      "50": { "&[data-highlighted]": { _before: { rounded: "50" } } },
      "100": { "&[data-highlighted]": { _before: { rounded: "100" } } },
      "200": { "&[data-highlighted]": { _before: { rounded: "200" } } },
      "250": { "&[data-highlighted]": { _before: { rounded: "250" } } },
      "300": { "&[data-highlighted]": { _before: { rounded: "300" } } },
      "350": { "&[data-highlighted]": { _before: { rounded: "350" } } },
      "400": { "&[data-highlighted]": { _before: { rounded: "400" } } },
      full: { "&[data-highlighted]": { _before: { rounded: "full" } } },
    },
  },
  defaultVariants: { rounded: "0" },
});

/**
 * A recipe for displaying a focus ring around an element without a border during keyboard navigation.
 *
 * Requires `FocusVisibleProvider` to be mounted in the app for proper keyboard detection.
 */
export const borderlessFocusRing = cva({
  base: {
    position: "relative",
    _focusVisible: {
      outline: "none",
    },
    _keyboardMode: {
      _focusVisible: {
        _before: {
          ...focusRingStyles,
          top: "-4", // 2px for focus ring + 2px offset
          left: "-4", // 2px for focus ring + 2px offset
          height: "[calc(100% + 8px)]", // height + offset * 2
          width: "[calc(100% + 8px)]", // width + offset * 2
        },
      },
    },
  },
  variants: {
    rounded: {
      // Unfortunately we can't create this map by looping through the radius tokens because
      // Panda CSS's compiler won't be able to parse it at build time
      "0": { _keyboardMode: { _focusVisible: { _before: { rounded: "0" } } } },
      "50": { _keyboardMode: { _focusVisible: { _before: { rounded: "50" } } } },
      "100": { _keyboardMode: { _focusVisible: { _before: { rounded: "100" } } } },
      "200": { _keyboardMode: { _focusVisible: { _before: { rounded: "200" } } } },
      "250": { _keyboardMode: { _focusVisible: { _before: { rounded: "250" } } } },
      "300": { _keyboardMode: { _focusVisible: { _before: { rounded: "300" } } } },
      "350": { _keyboardMode: { _focusVisible: { _before: { rounded: "350" } } } },
      "400": { _keyboardMode: { _focusVisible: { _before: { rounded: "400" } } } },
      full: { _keyboardMode: { _focusVisible: { _before: { rounded: "full" } } } },
    },
  },
  defaultVariants: { rounded: "250" },
});
